import { Circle } from '@mui/icons-material';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  RadioGroupProps,
  FormControl,
  Box,
} from '@mui/material';
import { InputHTMLAttributes, ReactNode } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HelperText } from '@components';

interface IControlledRadioGroup extends RadioGroupProps {
  fields: { [key: string]: string };
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  renderLabelSuffix?: (key: string, label: string) => ReactNode;
}

export const ControlledRadioGroup = ({
  fields,
  name,
  onChange,
  renderLabelSuffix,
  ...props
}: IControlledRadioGroup) => {
  const { control } = useFormContext();
  const { t } = useTranslation('common');

  return (
    <FormControl component="fieldset">
      <HelperText>{t('selectOne')}</HelperText>
      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <RadioGroup
            {...field}
            {...props}
            value={field.value || ''}
            onChange={(e) => {
              field.onChange(e);
              if (onChange) onChange(e);
            }}
          >
            {Object.entries(fields).map(([key, label]) => (
              <FormControlLabel
                key={key}
                value={key}
                sx={{
                  '.MuiFormControlLabel-label': {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                  },
                }}
                control={
                  <Radio
                    sx={{ mx: 1 }}
                    checkedIcon={<Circle />}
                    inputProps={
                      {
                        'data-cy': name + 'Radio',
                      } as InputHTMLAttributes<HTMLInputElement>
                    }
                    disableRipple
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                    }}
                  >
                    {label}
                    {renderLabelSuffix && (
                      <Box
                        sx={{
                          ml: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {renderLabelSuffix(key, label)}
                      </Box>
                    )}
                  </Box>
                }
              />
            ))}
          </RadioGroup>
        )}
      />
    </FormControl>
  );
};
